import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import PWAPrompt from './components/PWAPrompt.jsx';
import DebugViewport from './components/common/DebugViewport.jsx';
import BotonAtras from './components/BotonAtras.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import AvisoGlobal from './components/AvisoGlobal.jsx';
import AvisoActualizacion from './components/AvisoActualizacion.jsx';
import { useAuthStore }    from './store/authStore.js';
import { useSessionStore } from './store/sessionStore.js';
import { iniciarDrenado, registrarConfirmacion } from './store/outboxStore.js';
import { thermalPrinter }  from './native/thermalPrinter.js';
import { usePrinterStore } from './store/printerStore.js';
import { useTabsStore }    from './store/tabsStore.js';
import { usarModo, usarFaltaElegirModo, esComandera } from './store/deviceModeStore.js';
import { usarPlan } from './lib/plan.js';
import { getTenantActual } from './api/auth.api.js';
import { useSyncStore }    from './store/syncStore.js';
import { useHubStore, ensureHubSocket, autoDetectHub, onHubEvent } from './lib/hub.js';
import { buscarCajaPrincipal } from './lib/enlace.js';
import { startEmbeddedHub, embeddedHubAvailable, LOCAL_HUB_URL } from './native/hubRuntime.js';

import LoginScreen       from './screens/LoginScreen.jsx';
import OpenSessionScreen from './screens/OpenSessionScreen.jsx';
import CatalogScreen     from './screens/CatalogScreen.jsx';
import CheckoutScreen    from './screens/CheckoutScreen.jsx';
import ReceiptScreen     from './screens/ReceiptScreen.jsx';
import CloseSessionScreen  from './screens/CloseSessionScreen.jsx';
import CorteScreen         from './screens/CorteScreen.jsx';
import PrinterSetupScreen  from './screens/PrinterSetupScreen.jsx';
import RefundScreen        from './screens/RefundScreen.jsx';
import ReaderPickerScreen  from './screens/ReaderPickerScreen.jsx';
import SalesHistoryScreen     from './screens/SalesHistoryScreen.jsx';
import SessionsHistoryScreen  from './screens/SessionsHistoryScreen.jsx';
import TabsScreen             from './screens/TabsScreen.jsx';
import KitchenScreen          from './screens/KitchenScreen.jsx';
import PendingScreen     from './screens/PendingScreen.jsx';
import TableMapScreen         from './screens/TableMapScreen.jsx';
import HubSetupScreen         from './screens/HubSetupScreen.jsx';
import ModoAparatoScreen      from './screens/ModoAparatoScreen.jsx';

function useHydrated() {
  const [hydrated, setHydrated] = useState(
    () => useAuthStore.persist.hasHydrated() && useSessionStore.persist.hasHydrated()
  );
  useEffect(() => {
    if (useAuthStore.persist.hasHydrated() && useSessionStore.persist.hasHydrated()) {
      setHydrated(true);
      return;
    }
    let authDone = useAuthStore.persist.hasHydrated();
    let sessDone = useSessionStore.persist.hasHydrated();
    const check = () => { if (authDone && sessDone) setHydrated(true); };
    const u1 = useAuthStore.persist.onFinishHydration(() => { authDone = true; check(); });
    const u2 = useSessionStore.persist.onFinishHydration(() => { sessDone = true; check(); });
    return () => { u1(); u2(); };
  }, []);
  return hydrated;
}

// Puente con la caja central: mantiene el WebSocket vivo, carga las cuentas
// compartidas y reparte los eventos a las stores. Vive dentro del Router para
// que exista en todas las pantallas.
function HubBridge() {
  const token   = useAuthStore((s) => s.token);
  const hubUrl  = useHubStore((s) => s.hubUrl);
  const session = useSessionStore((s) => s.session);

  // Solo en el navegador: la caja central también sirve el POS por
  // http://IP:4747 y ahí la página se amarra sola. En la app nunca coincide y
  // solo gastaba 4 s probando https://localhost:4747 en cada arranque.
  useEffect(() => { if (!Capacitor.isNativePlatform()) autoDetectHub(); }, []);

  // iOS (PWA instalada y WKWebView) desplaza la ventana para dejar a la vista
  // el campo que recibe el teclado y, con `overflow: hidden` en el body, no la
  // devuelve al cerrarlo: el pie de la app quedaba 60 px por encima del borde
  // y abajo asomaba el fondo. Al perder el foco y al cambiar el viewport
  // visual se vuelve al origen.
  useEffect(() => {
    const volver = () => { if (window.scrollY || window.scrollX) window.scrollTo(0, 0); };
    document.addEventListener('focusout', volver);
    window.visualViewport?.addEventListener('resize', volver);
    return () => {
      document.removeEventListener('focusout', volver);
      window.visualViewport?.removeEventListener('resize', volver);
    };
  }, []);

  // La cola de órdenes sin confirmar. Arranca con la app a propósito: el caso
  // que más importa es el mesero que cerró la aplicación con platillos sin
  // subir, y ese solo se recupera aquí.
  useEffect(() => {
    // Cuando la cola confirma una cuenta hay que mover la base del carrito a
    // lo que quedó guardado. Si no, la siguiente diferencia se mide contra una
    // base vieja y se duplica lo ya subido.
    registrarConfirmacion((guardada) => {
      // La base se mueve PRIMERO. Si se llamara `applyUpsert` antes, su
      // `_reconcileCart` fundiría contra la base vieja y volvería a sumar la
      // diferencia que la cola acaba de subir: el carrito saltaría a +2 y el
      // autoguardado lo mandaría al servidor. Con la base ya en la versión
      // guardada, `_reconcileCart` sale solo porque las versiones coinciden.
      if (useTabsStore.getState().base?.id === guardada.id) {
        useTabsStore.setState({ base: { id: guardada.id, version: guardada.version, items: guardada.items } });
      }
      useTabsStore.getState().applyUpsert(guardada);
    });
    return iniciarDrenado();
  }, []);

  // Si esta tablet quedó configurada como caja, el hub embebido arranca con
  // la app, antes de que nadie toque nada: las demás tablets dependen de él.
  useEffect(() => {
    const { embedded, hubName } = useHubStore.getState();
    if (!embedded || !embeddedHubAvailable()) return;
    startEmbeddedHub({ name: hubName ?? 'Caja (tablet)' })
      .then((st) => useHubStore.getState().setHub(LOCAL_HUB_URL, st.name))
      .catch((err) => console.warn('[hub] no arrancó la caja embebida:', err.message));
  }, []);

  useEffect(() => { ensureHubSocket(token); }, [token, hubUrl]);

  // ── Enlace automático con la caja principal ─────────────────────────────
  // Al iniciar sesión, un aparato que no es la caja busca solo la caja
  // principal de su comercio (la nube sabe en qué IP de la red contesta) y se
  // conecta. Antes había que teclear la IP en "Este aparato". No se hace en una
  // caja con turno abierto por internet: cambiarla de camino a media jornada
  // no le toca a nadie más que a quien la atiende.
  useEffect(() => {
    if (!token) return undefined;
    const hub = useHubStore.getState();
    if (hub.hubUrl || hub.embedded || !hub.autoEnlace) return undefined;
    if (session && !esComandera()) return undefined;
    let vigente = true;
    buscarCajaPrincipal().then((caja) => {
      if (!vigente || !caja || useHubStore.getState().hubUrl) return;
      useHubStore.getState().setHub(caja.url, caja.name, caja.id);
      useHubStore.getState().avisar(`Conectado a la caja principal «${caja.name}»`);
      useSessionStore.getState().revalidar();
    }).catch(() => {});
    return () => { vigente = false; };
  }, [token, session?._id]);

  // Si la caja guardada deja de contestar (el router le dio otra IP), se le
  // pregunta a la nube dónde está ahora y se cambia sola a la nueva dirección.
  const conectado = useHubStore((s) => s.connected);
  const embebida  = useHubStore((s) => s.embedded);
  useEffect(() => {
    if (!token || !hubUrl || embebida || conectado) return undefined;
    const t = setInterval(() => {
      const { hubId, hubUrl: actual } = useHubStore.getState();
      buscarCajaPrincipal({ preferirId: hubId }).then((caja) => {
        if (!caja || caja.url === useHubStore.getState().hubUrl || actual !== useHubStore.getState().hubUrl) return;
        useHubStore.getState().setHub(caja.url, caja.name, caja.id);
        useHubStore.getState().avisar(`La caja principal cambió de dirección; ya estás conectado otra vez`);
      }).catch(() => {});
    }, 20_000);
    return () => clearInterval(t);
  }, [token, hubUrl, embebida, conectado]);

  // Cuentas: foto inicial + cambios en vivo
  useEffect(() => {
    // Las cuentas son del comercio, no del turno: la comandera no abre caja
    // y aun así tiene que recuperar su mesa activa al arrancar. Atado a la
    // sesión, el teléfono decía "Cuenta vacía" hasta pasar por /tabs.
    if (!token) return;
    const tabs = useTabsStore.getState();
    tabs.load();
    const offs = [
      onHubEvent('tabs.snapshot', (list) => tabs.applySnapshot(list ?? [])),
      onHubEvent('tab.upsert',    (tab)  => tabs.applyUpsert(tab)),
      onHubEvent('tab.close',     ({ id, reason }) => tabs.applyClose(id, reason)),
      onHubEvent('connected',     (ok)   => { if (ok) tabs.load(); }),
      // Totales del turno que suben las demás tablets
      onHubEvent('session', (s) => {
        if (!s) return;
        const cur = useSessionStore.getState().session;
        if (cur && (cur._id === s._id || cur.client_uuid === s.client_uuid)) useSessionStore.getState().updateSessionTotals(s);
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [token, session?._id]);

  // Sin caja central, el indicador refleja la red de la tablet
  useEffect(() => {
    if (hubUrl) return;
    const sync = useSyncStore.getState();
    const on  = () => sync.setOnline(true);
    const off = () => sync.setOnline(false);
    sync.setOnline(navigator.onLine);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, [hubUrl]);

  return null;
}

// Indicador de medidas del viewport para diagnosticar en un teléfono real,
// donde no hay consola. Se enciende abriendo la app con `?vp=1` (queda
// guardado) y se apaga con `?vp=0`.
function debugViewport() {
  try {
    const q = new URLSearchParams(window.location.search).get('vp');
    if (q === '1') localStorage.setItem('gc-debug-vp', '1');
    if (q === '0') localStorage.removeItem('gc-debug-vp');
    return localStorage.getItem('gc-debug-vp') === '1';
  } catch { return false; }
}

// Un restaurante que entra por primera vez en este aparato elige primero para
// qué es (caja o comandera): de eso depende a dónde lo lleva todo lo demás.
function PreguntarModo({ children }) {
  const falta = usarFaltaElegirModo();
  const { pathname } = useLocation();
  if (falta && pathname !== '/aparato') return <Navigate to="/aparato" replace />;
  return children;
}

function PrivateRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hydrated = useHydrated();
  if (!hydrated) return null;
  return isAuthenticated ? <PreguntarModo>{children}</PreguntarModo> : <Navigate to="/login" replace />;
}

// Dónde empieza el mesero.
//
// La primera pregunta de quien trae una comandera no es "qué platillo", es
// "qué mesa": arrancar en el catálogo lo obliga a salirse antes de poder hacer
// nada. Con una cuenta ya abierta sí manda el catálogo, que es donde va a
// pasar el rato agregando.
// Abrir turno es declarar el efectivo con el que empieza un cajón. La
// comandera no tiene cajón, así que si llega aquí por cualquier camino se le
// devuelve a su plano en vez de pedirle un dato que no le corresponde.
function SinTurnoEnComandera() {
  const modo = usarModo();
  if (modo === 'comandera') return <Navigate to="/tables" replace />;
  return <OpenSessionScreen />;
}

function InicioSegunAparato() {
  const modo        = usarModo();
  const activeTabId = useTabsStore((s) => s.activeTabId);
  if (modo === 'comandera' && !activeTabId) return <Navigate to="/tables" replace />;
  return <CatalogScreen />;
}

function SessionRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const session         = useSessionStore((s) => s.session);
  const modo            = usarModo();
  const hydrated = useHydrated();
  if (!hydrated) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // El celular del mesero no abre turno: no tiene cajón que cuadrar. Mandarlo
  // a /open-session era pedirle que declarara el efectivo con el que empieza.
  if (modo === 'comandera') return <PreguntarModo>{children}</PreguntarModo>;
  if (!session)         return <PreguntarModo><Navigate to="/open-session" replace /></PreguntarModo>;
  return <PreguntarModo>{children}</PreguntarModo>;
}

// Mesas y cocina son del servicio en mesa. Si el giro o el plan no lo traen,
// la pantalla no existe para este comercio: se le devuelve al catálogo en vez
// de dejarlo frente a un error del servidor.
function SoloServicioEnMesa({ children }) {
  const { servicioEnMesa } = usarPlan();
  if (!servicioEnMesa) return <Navigate to="/" replace />;
  return children;
}

// La pantalla de cocina, además, solo si el negocio usa cocina (ajuste del
// comercio): sin él no hay comandas que mostrar.
function SoloConCocina({ children }) {
  const { cocina } = usarPlan();
  if (!cocina) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  useEffect(() => {
    // El lector Stripe NO se prepara aquí: pedía la ubicación al abrir la app
    // en todos los aparatos —también en los que cobran con terminal propia— y,
    // si se negaba, el plugin la volvía a pedir sin fin y congelaba la app. Se
    // prepara solo al buscar un lector o cobrar con él (native/stripeTerminal).

    // Reconecta la impresora guardada sin pedir el selector, para que
    // el auto-imprimir del checkout funcione tras recargar la app.
    const { deviceId, transport } = usePrinterStore.getState();
    thermalPrinter.init()
      .then(() => thermalPrinter.autoReconnect(deviceId, transport))
      .catch((err) => console.warn('[Printer] autoReconnect:', err));
  }, []);

  // Comprobar que el turno guardado sigue abierto en el servidor.
  //
  // No basta con hacerlo al arrancar: una tablet de mostrador no se reinicia,
  // se queda encendida días. Se repite al volver a primer plano, que es cuando
  // el cajero retoma —y el momento en que pudo haber pasado algo mientras la
  // pantalla estaba apagada.
  const hidratado = useHydrated();
  useEffect(() => {
    // Hasta que el store no se hidrata, `session` todavía es null y revalidar
    // no vería nada que comprobar.
    if (!hidratado) return;

    // Además del turno, el comercio: su plan y lo que incluye pueden haber
    // cambiado (contrató otro plan, lo pasaron a restaurante). Sin esto la caja
    // seguiría con lo que traía al iniciar sesión hasta que alguien saliera y
    // volviera a entrar. Si no hay red no pasa nada: se queda con lo guardado.
    const refrescarComercio = () => {
      if (!useAuthStore.getState().isAuthenticated) return;
      getTenantActual()
        .then((tenant) => useAuthStore.getState().actualizarTenant(tenant))
        .catch(() => {});
    };

    const revalidar = () => {
      useSessionStore.getState().revalidar();
      refrescarComercio();
    };

    revalidar();

    let ultima = Date.now();
    const alVolver = () => {
      if (document.visibilityState !== 'visible') return;
      // Cambiar de app y volver no puede costar una petición cada vez.
      if (Date.now() - ultima < 60_000) return;
      ultima = Date.now();
      revalidar();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [hidratado]);

  return (
    <BrowserRouter>
      {debugViewport() && <DebugViewport />}
      <HubBridge />
      <BotonAtras />
      <AvisoGlobal />
      <AvisoActualizacion />
      <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/hub"   element={<HubSetupScreen />} />
        <Route path="/aparato" element={<PrivateRoute><ModoAparatoScreen /></PrivateRoute>} />

        <Route
          path="/open-session"
          element={
            <PrivateRoute>
              <SinTurnoEnComandera />
            </PrivateRoute>
          }
        />

        <Route
          path="/"
          element={
            <SessionRoute>
              <InicioSegunAparato />
            </SessionRoute>
          }
        />

        <Route
          path="/checkout"
          element={
            <SessionRoute>
              <CheckoutScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/receipt"
          element={
            <SessionRoute>
              <ReceiptScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/close-session"
          element={
            <PrivateRoute>
              <CloseSessionScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/corte"
          element={
            <PrivateRoute>
              <CorteScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/refund"
          element={
            <PrivateRoute>
              <RefundScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/printer-setup"
          element={
            <PrivateRoute>
              <PrinterSetupScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/reader-picker"
          element={
            <PrivateRoute>
              <ReaderPickerScreen />
            </PrivateRoute>
          }
        />

        <Route
          path="/sales-history"
          element={
            <SessionRoute>
              <SalesHistoryScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/sessions-history"
          element={
            <SessionRoute>
              <SessionsHistoryScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/tabs"
          element={
            <SessionRoute>
              <TabsScreen />
            </SessionRoute>
          }
        />

        <Route
          path="/tables"
          element={
            <SessionRoute>
              <SoloServicioEnMesa>
                <TableMapScreen />
              </SoloServicioEnMesa>
            </SessionRoute>
          }
        />

        <Route
          path="/kitchen"
          element={
            <PrivateRoute>
              <SoloConCocina>
                <KitchenScreen />
              </SoloConCocina>
            </PrivateRoute>
          }
        />

        {/* Sin sesión: la comandera también necesita ver lo que no ha salido. */}
        <Route
          path="/pendientes"
          element={
            <PrivateRoute>
              <PendingScreen />
            </PrivateRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>
      {!Capacitor.isNativePlatform() && <PWAPrompt />}
    </BrowserRouter>
  );
}
